import { BOARD_CONST } from "../modules/s25-swarm-schedule/s25.board.const";
import { OccurrenceI } from "../pojo/OccurrenceI";
import { Proto } from "../pojo/Proto";
import { OccurrenceModel } from "../pojo/ReservationI";
import { S25ItemI } from "../pojo/S25ItemI";
import { EventProfile, EventReservation } from "../services/event.service";
import { S25Util } from "./s25-util";
import DowNumber = Proto.DowNumber;

export class EventCopyOccurrenceUtil {
    public static applySegmentCopyToProfile(
        profile: EventProfile,
        segment: {
            mapping?: OccurrenceI.Mapping;
            profileCode?: string;
            occurrences: OccurrenceModel[];
        },
    ): void {
        const originalReservations = EventCopyOccurrenceUtil.sortReservations(profile.reservation ?? []);
        const mapping = segment.mapping ?? segment.occurrences[0]?.mapping ?? "one-by-one";
        const spaceReservationById = EventCopyOccurrenceUtil.indexSpaceReservations(originalReservations);
        const resourceReservationById = EventCopyOccurrenceUtil.indexResourceReservations(originalReservations);

        profile.reservation = segment.occurrences.map((occurrence, index) => {
            const sourceReservation =
                EventCopyOccurrenceUtil.getSourceReservation(originalReservations, mapping, occurrence, index) ??
                originalReservations[0];
            const reservation = sourceReservation ? S25Util.deepCopy(sourceReservation) : ({} as EventReservation);

            reservation.reservation_start_dt = S25Util.date.toS25ISODateTimeStr(
                occurrence.startDt,
            ) as EventReservation["reservation_start_dt"];
            reservation.reservation_end_dt = S25Util.date.toS25ISODateTimeStr(
                occurrence.endDt,
            ) as EventReservation["reservation_end_dt"];
            reservation.event_start_dt = reservation.reservation_start_dt;
            reservation.event_end_dt = reservation.reservation_end_dt;

            reservation.space_reservation = EventCopyOccurrenceUtil.buildSpaceReservations(
                occurrence.locations,
                spaceReservationById,
            );
            reservation.resource_reservation = EventCopyOccurrenceUtil.buildResourceReservations(
                occurrence.resources,
                resourceReservationById,
            );

            return reservation;
        });

        const firstOccurrence = segment.occurrences[0];
        if (firstOccurrence?.startDt && firstOccurrence.endDt) {
            // Patterned / single-occ segments display from init_* dates (not reservations).
            // Mirror event-form-put so mapped copy dates actually come through.
            profile.init_start_dt = S25Util.date.toS25ISODateTimeStr(
                firstOccurrence.startDt,
            ) as EventProfile["init_start_dt"];
            profile.init_end_dt = S25Util.date.toS25ISODateTimeStr(
                firstOccurrence.endDt,
            ) as EventProfile["init_end_dt"];
        }

        if (segment.occurrences.length === 1 && firstOccurrence?.startDt) {
            profile.rec_type_id = 0;
            profile.profile_code = EventCopyOccurrenceUtil.singleOccurrenceProfileCode(firstOccurrence.startDt);
        } else if (segment.profileCode) {
            profile.profile_code = segment.profileCode;
        }
    }

    /** Same shape as event-form-put single-occ profile_code (W1 {DOW} {YYYYMMDD}T235900). */
    public static singleOccurrenceProfileCode(startDt: Date | string): string {
        const startDate = S25Util.date.parseDropTZ(startDt);
        const dayOfWeek = startDate.getDay() as DowNumber;
        const datePart = S25Util.date.toS25ISODateStr(startDate)?.replace(/-/g, "") ?? "";
        return "W1 " + BOARD_CONST.dowInt2Abbr[dayOfWeek] + " " + datePart + "T235900";
    }

    /** Shift all reservation / init dates on a profile by a whole-day offset (related-event copy). */
    public static shiftProfileByDays(profile: EventProfile, dayOffset: number): void {
        if (!dayOffset) {
            return;
        }

        const shiftDate = (value: string | undefined): string | undefined => {
            if (!value) {
                return value;
            }
            return S25Util.date.toS25ISODateTimeStr(S25Util.date.addDays(value, dayOffset));
        };

        for (const reservation of profile.reservation ?? []) {
            reservation.reservation_start_dt = shiftDate(
                reservation.reservation_start_dt,
            ) as EventReservation["reservation_start_dt"];
            reservation.reservation_end_dt = shiftDate(
                reservation.reservation_end_dt,
            ) as EventReservation["reservation_end_dt"];
            reservation.event_start_dt = shiftDate(reservation.event_start_dt) as EventReservation["event_start_dt"];
            reservation.event_end_dt = shiftDate(reservation.event_end_dt) as EventReservation["event_end_dt"];
        }

        profile.init_start_dt = shiftDate(profile.init_start_dt) as EventProfile["init_start_dt"];
        profile.init_end_dt = shiftDate(profile.init_end_dt) as EventProfile["init_end_dt"];

        if (profile.reservation?.length === 1 && profile.reservation[0]?.reservation_start_dt) {
            profile.rec_type_id = 0;
            profile.profile_code = EventCopyOccurrenceUtil.singleOccurrenceProfileCode(
                profile.reservation[0].reservation_start_dt,
            );
        }
    }

    private static sortReservations(reservations: EventReservation[]): EventReservation[] {
        return [...reservations].sort(
            (left, right) =>
                S25Util.date.parse(left.reservation_start_dt).getTime() -
                S25Util.date.parse(right.reservation_start_dt).getTime(),
        );
    }

    private static getSourceReservation(
        originalReservations: EventReservation[],
        mapping: OccurrenceI.Mapping,
        occurrence: OccurrenceModel,
        index: number,
    ): EventReservation | undefined {
        if (!originalReservations.length) {
            return undefined;
        }

        if (mapping === "date-by-date") {
            const occurrenceDate = S25Util.date.toS25ISODateStr(occurrence.startDt);
            return (
                originalReservations.find(
                    (reservation) => S25Util.date.toS25ISODateStr(reservation.reservation_start_dt) === occurrenceDate,
                ) ?? originalReservations[0]
            );
        }

        if (mapping === "none") {
            return originalReservations[0];
        }

        return originalReservations[index] ?? originalReservations[0];
    }

    private static indexSpaceReservations(reservations: EventReservation[]) {
        const spaceReservationById = new Map<number, NonNullable<EventReservation["space_reservation"]>[number]>();
        for (const reservation of reservations) {
            for (const spaceReservation of reservation.space_reservation ?? []) {
                spaceReservationById.set(parseInt(String(spaceReservation.space_id)), spaceReservation);
            }
        }
        return spaceReservationById;
    }

    private static indexResourceReservations(reservations: EventReservation[]) {
        const resourceReservationById = new Map<
            number,
            NonNullable<EventReservation["resource_reservation"]>[number]
        >();
        for (const reservation of reservations) {
            for (const resourceReservation of reservation.resource_reservation ?? []) {
                resourceReservationById.set(parseInt(String(resourceReservation.resource_id)), resourceReservation);
            }
        }
        return resourceReservationById;
    }

    private static buildSpaceReservations(
        locations: S25ItemI[] | undefined,
        spaceReservationById: Map<number, NonNullable<EventReservation["space_reservation"]>[number]>,
    ): EventReservation["space_reservation"] {
        return (locations ?? [])
            .map((location) => spaceReservationById.get(Number(location.itemId)))
            .filter(S25Util.isDefined);
    }

    private static buildResourceReservations(
        resources: S25ItemI[] | undefined,
        resourceReservationById: Map<number, NonNullable<EventReservation["resource_reservation"]>[number]>,
    ): EventReservation["resource_reservation"] {
        return (resources ?? [])
            .map((resource) => resourceReservationById.get(Number(resource.itemId)))
            .filter(S25Util.isDefined);
    }
}
